import useTranslation from 'next-translate/useTranslation'
import {
  OrderFormat,
  OutletState,
  ServiceTime,
  ServiceTimeKindType,
  ErrorCode,
  BoxPickUpStation,
} from '@ancon/wildcat-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { useRouter } from 'next/router'
import { useSearchParams } from 'next/navigation'
import { batch } from 'react-redux'
import createCodedError from '@ancon/wildcat-utils/error/createCodedError'
import noop from 'lodash/noop'
import usePrevious from '@ancon/wildcat-utils/shared/hooks/usePrevious'

import HeadingText from '../../../app/components/HeadingText'
import BodyText from '../../../app/components/BodyText'
import Modal from '../../../app/components/modal/Modal'
import OrderTimeSelectionStep from '../../../outlet/components/orderConfigurationModal/OrderTimeSelectionStep'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { outletsSelectedOutletListItemSelector } from '../../store/outletsSelectors'
import OrderConfigurationModalHeader from '../../../outlet/components/orderConfigurationModal/OrderConfigurationModalHeader'
import { ModalHeaderProps } from '../../../app/components/modal/ModalHeader'
import {
  AppRoutes,
  AppTestIds,
  OrderFormatSlugMap,
} from '../../../app/constants'
import Button from '../../../app/components/Button'
import useRouteMatch from '../../../app/hooks/useRouteMatch'
import {
  outletFiltersSectionSelector,
  outletFiltersServiceTimeSelector,
  outletListItemSelector,
} from '../../../outlet/store/outletSelector'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import {
  outletSetIsVisibleServiceTimeKindSelectionModal,
  outletSetOutletFilters,
} from '../../../outlet/store/outletSlice'
import getTranslatedError from '../../../app/utils/getTranslatedError'
import { appShowError } from '../../../app/store/appSlice'
import { ErrorModalType, WhiteLabeledAppName } from '../../../app/types'
import BoxPickupSections from '../../../outlet/components/boxPickupOrderConfigureModal/BoxPickupSections'
import { fetchOutletStations } from '../../../outlet/store/outletThunks'
import isAppWhiteLabeled from '../../../app/utils/isAppWhiteLabeled'
import getWhiteLabelClassName from '../../../app/utils/getWhiteLabelClassName'
import hasOutletDetailsURLFlag from '../../../outlet/utils/hasOutletDetailsURLFlag'
import isOutletInPreOrderOnlyMode from '../../../outlet/utils/isOutletInPreOrderOnlyMode'

import OrderConfigureTimeInfo from './OrderConfigureTimeInfo'
import styles from './NewOrderTimeConfigureModal.module.scss'

const initialServiceTime = {
  kind: ServiceTimeKindType.AsSoonAsPossible,
  time: '',
}

type NewOrderTimeConfigureModalProps = {
  selectedOrderFormat?: OrderFormat
  onClose: () => void
  isOpen?: boolean
}

export default function NewOrderTimeConfigureModal({
  selectedOrderFormat,
  onClose,
  isOpen = false,
}: NewOrderTimeConfigureModalProps) {
  const isBoxPickup = selectedOrderFormat === OrderFormat.BoxPickup
  const isTableOrder = selectedOrderFormat === OrderFormat.TableOrder
  const prevIsBoxPickup = usePrevious(isBoxPickup)
  const { t, lang } = useTranslation('common')
  const router = useRouter()
  const searchParams = useSearchParams()
  const { query } = router
  const dispatch = useAppDispatch()
  const [isShowTimePicker, setIsShowTimePicker] = useState(
    !query?.serviceTime ? !!isBoxPickup : false,
  )
  const [serviceTime, setServiceTime] = useState<ServiceTime>(
    isBoxPickup
      ? { kind: ServiceTimeKindType.AtSpecifiedTime, time: '' }
      : initialServiceTime,
  )
  const [selectedSection, setSelectedSection] = useState<BoxPickUpStation>()
  const [sections, setSections] = useState<BoxPickUpStation[]>([])
  const [isSectionsFetching, setIsSectionsFetching] = useState(false)
  const isOutletPage = useRouteMatch([`${AppRoutes.Outlet}/`])
  const outletsOutletListItem = useAppSelector(
    outletsSelectedOutletListItemSelector,
  )
  const outletOutletListItem = useAppSelector(outletListItemSelector)
  const outletFilterServiceTime = useAppSelector(
    outletFiltersServiceTimeSelector,
  )
  const outletFiltersSection = useAppSelector(outletFiltersSectionSelector)
  const outletListItem = isOutletPage
    ? outletOutletListItem
    : outletsOutletListItem
  const isOrderNow = serviceTime.kind === ServiceTimeKindType.AsSoonAsPossible
  const pickerTimeMoment = serviceTime?.time
    ? moment(serviceTime.time)
    : undefined

  const isInPreOrderOnlyMode = isOutletInPreOrderOnlyMode(outletListItem)

  const isFirstStep = !isShowTimePicker || (isBoxPickup && !pickerTimeMoment)

  const isWhiteLabeled = isAppWhiteLabeled()

  useEffect(() => {
    async function showOutletClosedError() {
      const error = await getTranslatedError(
        createCodedError(
          'Restaurant not open yet',
          ErrorCode.RequestedOutletIsNotOpen,
        ),
        undefined,
        lang,
      )
      dispatch(
        appShowError({
          ...error,
          errorModalType: ErrorModalType.OutletNotAvailableForOrders,
        }),
      )
    }

    const hasOutletDetailsFlag = hasOutletDetailsURLFlag(searchParams)

    if (
      isOutletPage &&
      !outletFilterServiceTime &&
      query?.serviceTime === undefined &&
      !hasOutletDetailsFlag
    ) {
      if (outletOutletListItem) {
        const isClosed = outletOutletListItem.state === OutletState.Closed

        if (isClosed) {
          showOutletClosedError()
        } else if (!isTableOrder) {
          dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
        }
      }
    } else if (
      isOutletPage &&
      outletFilterServiceTime &&
      isBoxPickup &&
      !outletFiltersSection &&
      !hasOutletDetailsFlag
    ) {
      // Show section selection modal
      dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    outletFilterServiceTime,
    isOutletPage,
    query?.serviceTime,
    dispatch,
    outletOutletListItem,
    lang,
    outletFiltersSection,
    isBoxPickup,
  ])

  useEffect(() => {
    if (prevIsBoxPickup !== isBoxPickup && !query?.serviceTime) {
      setIsShowTimePicker(!!isBoxPickup)
    }
  }, [isBoxPickup, prevIsBoxPickup, query?.serviceTime])

  useEffect(() => {
    // Reset state when modal is closed
    if (!isOpen) {
      setSelectedSection(undefined)
    }
  }, [isOpen])

  useEffect(() => {
    // Fetch sections
    if (isOpen && isBoxPickup && isOutletPage) {
      setIsSectionsFetching(true)
      dispatch(fetchOutletStations({ outletId: outletListItem?.id! }))
        .unwrap()
        .then(({ items }) => {
          setSections(items)
        })
        .catch(noop)
        .finally(() => {
          setIsSectionsFetching(false)
        })
    }
  }, [dispatch, isBoxPickup, isOpen, outletListItem?.id, isOutletPage])

  function handleOnPressBack() {
    setIsShowTimePicker(false)
  }

  const CustomHeader = useCallback(
    (headerProps: ModalHeaderProps) => (
      <OrderConfigurationModalHeader
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...headerProps}
        onBack={handleOnPressBack}
        isSecondStep={!isFirstStep}
        className={clsx({
          [styles.header]: !isFirstStep,
        })}
      />
    ),
    [isFirstStep],
  )

  function handleOnChangeServiceTime(newServiceTime: ServiceTime) {
    const isNavigateToOutletPage = !isOutletPage && outletListItem?.urlSlug
    if (isNavigateToOutletPage) {
      const { urlSlug } = outletListItem
      router.push({
        pathname: `outlet/${urlSlug}`,
        query: {
          orderFormat: OrderFormatSlugMap[selectedOrderFormat as never],
          serviceTime: newServiceTime.time,
        },
      })
    } else {
      setServiceTime(newServiceTime)
      setIsShowTimePicker(false)
    }
  }

  function handleOnClickOrderNow() {
    setServiceTime({
      kind: ServiceTimeKindType.AsSoonAsPossible,
      time: '',
    })
  }

  function handleOnClickOrderLater() {
    setServiceTime({
      kind: ServiceTimeKindType.AtSpecifiedTime,
      time: '',
    })
  }

  useEffect(() => {
    if (isOpen) {
      const filterServiceTime = outletFilterServiceTime?.time
      const serviceTimeKind =
        isInPreOrderOnlyMode || !!filterServiceTime
          ? ServiceTimeKindType.AtSpecifiedTime
          : ServiceTimeKindType.AsSoonAsPossible

      setServiceTime({
        kind: serviceTimeKind,
        time: filterServiceTime || '',
      })
    }
  }, [isInPreOrderOnlyMode, isOpen, outletFilterServiceTime?.time])

  function handleOnClickScheduleOrder() {
    setIsShowTimePicker(true)
  }

  function handleOnClickContinue() {
    if (isOutletPage) {
      batch(() => {
        dispatch(
          outletSetOutletFilters({
            orderFormat: selectedOrderFormat,
            serviceTime,
            section: selectedSection,
          }),
        )
        dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(false))
      })
    } else if (outletListItem?.urlSlug) {
      const { urlSlug } = outletListItem
      router.push({
        pathname: `outlet/${urlSlug}`,
        query: {
          orderFormat: OrderFormatSlugMap[selectedOrderFormat as never],
          serviceTime: serviceTime.time,
        },
      })
    }
  }

  function handleOnClose() {
    onClose()
    setServiceTime(initialServiceTime)
    setIsShowTimePicker(false)
  }

  const isContinueButtonDisabled = useMemo(() => {
    const isServiceTimeNotAdded = !isOrderNow && !serviceTime?.time

    if (isBoxPickup) {
      return isServiceTimeNotAdded || !selectedSection
    }

    return isServiceTimeNotAdded
  }, [isBoxPickup, isOrderNow, selectedSection, serviceTime?.time])

  function getTimeSwitchButtonVariant(isActive: boolean) {
    if (isWhiteLabeled) {
      return 'secondary'
    }

    return isActive ? 'tertiary' : 'secondary'
  }

  const isPreOrderOnly = isBoxPickup || isInPreOrderOnlyMode

  return (
    <Modal
      isOpen={isOpen}
      title={t(
        isShowTimePicker
          ? 'newOrderTimeConfigureModal.scheduleOrder'
          : 'newOrderTimeConfigureModal.title',
      )}
      onClose={handleOnClose}
      className={styles.modal}
      mobileModalMode={isShowTimePicker ? 'full-screen' : 'bottom-sheet'}
      CustomModalHeader={CustomHeader}
      preventOnClose={isBoxPickup && isOutletPage}
    >
      {isShowTimePicker ? (
        <OrderTimeSelectionStep
          selectedOrderFormat={selectedOrderFormat!}
          initialServiceTime={serviceTime}
          outletListItem={outletListItem!}
          onApply={handleOnChangeServiceTime}
        />
      ) : (
        <div className={styles.wrapper}>
          <BodyText color="body-1" className={styles.infoText}>
            {t(
              isBoxPickup
                ? 'newOrderTimeConfigureModal.boxPickup.whenInfo'
                : 'newOrderTimeConfigureModal.info',
            )}
          </BodyText>

          {isPreOrderOnly ? (
            <HeadingText
              as="h3"
              color="heading-1"
              className={styles.schedulePreOrderTitle}
            >
              {t('orderTimeSwitch.later')}
            </HeadingText>
          ) : (
            <div
              className={clsx(
                styles.orderTimeSwitch,
                {
                  [styles.whiteLabeled]: isWhiteLabeled,
                },
                getWhiteLabelClassName({
                  [WhiteLabeledAppName.HeatRestauranger]: styles.heatApp,
                }),
              )}
            >
              <Button
                variant={getTimeSwitchButtonVariant(isOrderNow)}
                className={clsx({
                  [styles.active]: isOrderNow,
                })}
                onClick={handleOnClickOrderNow}
                disabled={isInPreOrderOnlyMode}
              >
                {t('orderTimeSwitch.orderNow')}
              </Button>
              <Button
                variant={getTimeSwitchButtonVariant(!isOrderNow)}
                className={clsx({
                  [styles.active]: !isOrderNow,
                })}
                onClick={handleOnClickOrderLater}
                data-testid={
                  AppTestIds.Order.Modal.NewOrderTimeConfigureModal
                    .OrderLaterButton
                }
              >
                {t('orderTimeSwitch.later')}
              </Button>
            </div>
          )}
          {(!isOrderNow || isBoxPickup) && (
            <section className={styles.dateTimeSection}>
              <OrderConfigureTimeInfo
                serviceTime={serviceTime}
                onClickScheduleOrder={handleOnClickScheduleOrder}
              />
              {isBoxPickup && (
                <BoxPickupSections
                  onChangeSection={setSelectedSection}
                  selectedSection={selectedSection}
                  sections={sections}
                  isLoading={isSectionsFetching}
                />
              )}
            </section>
          )}
          <Button
            className={styles.continueButton}
            disabled={isContinueButtonDisabled}
            onClick={handleOnClickContinue}
            data-testid={
              AppTestIds.Order.Modal.NewOrderTimeConfigureModal.ContinueButton
            }
          >
            {t('newOrderTimeConfigureModal.continue')}
          </Button>
        </div>
      )}
    </Modal>
  )
}
